import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtCVE } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Package, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { code: "", name: "", description: "", unit: "un", price: 0, vat_rate: 15, category: "", active: true, track_stock: false, stock_qty: 0, min_stock: 0 };

export default function Products() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { search: search || undefined } });
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) return toast.error("O nome é obrigatório");
    setBusy(true);
    try {
      const payload = { ...form, price: Number(form.price), vat_rate: Number(form.vat_rate), stock_qty: Number(form.stock_qty) || 0, min_stock: Number(form.min_stock) || 0 };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produto atualizado" : "Produto criado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminar "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Produto eliminado");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="products-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Catálogo</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Produtos e Serviços</h1>
        </div>
        <Button className="rounded-full" onClick={openNew} data-testid="new-product-btn">
          <Plus size={18} className="mr-1.5" /> Novo Produto
        </Button>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Pesquisar por nome, código ou categoria..." value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="products-search" />
      </div>

      <div className="rounded-xl border border-border bg-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <Package size={40} className="text-muted-foreground" />
            <p className="text-muted-foreground">Ainda não há produtos.</p>
            <Button variant="outline" className="rounded-full" onClick={openNew}>Adicionar primeiro produto</Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="hidden md:table-cell">Código</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead className="hidden lg:table-cell">Categoria</TableHead>
                <TableHead className="text-right">Preço</TableHead>
                <TableHead className="hidden md:table-cell text-right">IVA</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} className="stagger-item" data-testid={`product-row-${p.id}`}>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{p.code || "—"}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">{p.category || "—"}</TableCell>
                  <TableCell className="text-right font-medium">{fmtCVE(p.price)}</TableCell>
                  <TableCell className="hidden md:table-cell text-right text-muted-foreground">{p.vat_rate}%</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={p.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-600"}>
                      {p.active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(p)}
                      data-testid={`delete-product-${p.id}`}>
                      <Trash size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Código</Label>
              <Input value={form.code} onChange={set("code")} data-testid="product-code" />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Input value={form.category} onChange={set("category")} data-testid="product-category" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Nome *</Label>
              <Input value={form.name} onChange={set("name")} data-testid="product-name" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Descrição</Label>
              <Textarea value={form.description} onChange={set("description")} data-testid="product-description" />
            </div>
            <div className="space-y-1.5">
              <Label>Unidade</Label>
              <Input value={form.unit} onChange={set("unit")} data-testid="product-unit" />
            </div>
            <div className="space-y-1.5">
              <Label>Preço (CVE)</Label>
              <Input type="number" step="0.01" value={form.price} onChange={set("price")} data-testid="product-price" />
            </div>
            <div className="space-y-1.5">
              <Label>Taxa de IVA (%)</Label>
              <Input type="number" step="0.01" value={form.vat_rate} onChange={set("vat_rate")} data-testid="product-vat" />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-active" />
              <Label>Ativo</Label>
            </div>
            <div className="col-span-2 flex items-center gap-2 border-t border-border pt-3">
              <Switch checked={form.track_stock} onCheckedChange={(v) => setForm({ ...form, track_stock: v })} data-testid="product-track-stock" />
              <Label>Controlar stock</Label>
            </div>
            {form.track_stock && (
              <>
                <div className="space-y-1.5">
                  <Label>Stock inicial</Label>
                  <Input type="number" step="0.01" value={form.stock_qty} onChange={set("stock_qty")} disabled={!!editing} data-testid="product-stock-qty" />
                  {editing && <p className="text-[11px] text-muted-foreground">Altere o stock em Stock → Movimentar.</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Stock mínimo</Label>
                  <Input type="number" step="0.01" value={form.min_stock} onChange={set("min_stock")} data-testid="product-min-stock" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="save-product-btn">
              {busy ? <CircleNotch className="animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
